import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getStudentProgress, raiseClassOver } from '../lib/api';

// Raise Class Over for a student whose course is finished — mentors from My
// Students, Student Coordinators from their Class Over and Students pages. It
// takes effect straight away: the student is deactivated and admin is emailed
// the class report (server: raiseClassOver).

const ClassOverModal: React.FC<{
  studentId: string;
  studentName: string;
  course: string;
  raisedBy: string;   // mentorId of the mentor / coordinator raising it
  onDone: () => void;
  onClose: () => void;
}> = ({ studentId, studentName, course, raisedBy, onDone, onClose }) => {
  const [counts, setCounts] = useState<{ present: number; absent: number } | null>(null);
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getStudentProgress(studentId, raisedBy)
      .then(res => setCounts({
        present: res.records.filter(r => r.attendance === 'Present').length,
        absent:  res.records.filter(r => r.attendance === 'Absent').length,
      }))
      .catch(() => {});
  }, [studentId, raisedBy]);

  const raise = async () => {
    setSaving(true);
    try {
      await raiseClassOver(raisedBy, studentId, note.trim());
      toast.success(`Class Over raised for ${studentName} — admin has been notified`);
      onDone();
    } catch (err: any) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
        <h3 className="text-sm font-bold mb-1" style={{ color: '#2b2240' }}>🎓 Raise Class Over</h3>
        <p className="text-xs text-gray-400 mb-4">{studentName}{course ? ` · ${course}` : ''}</p>
        <div className="rounded-xl px-4 py-3 mb-4 text-xs text-gray-600 leading-relaxed" style={{ background: '#f8f6ff' }}>
          {counts
            ? <>{counts.present + counts.absent} classes logged ({counts.present} present, {counts.absent} absent). </>
            : 'Loading classes… '}
          {studentName.split(' ')[0]} will be marked as finished and made inactive, and admin gets their full class report.
        </div>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={1000}
          placeholder="Anything admin should know? (optional)"
          className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 mb-4 resize-none" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
          <button onClick={raise} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-50"
            style={{ background: '#0369a1' }}>
            {saving ? 'Raising…' : 'Raise Class Over'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassOverModal;
